/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import React from 'react';
import * as mobx from 'mobx';
import { observable } from 'mobx';
import CSSHidingTransition from 'components/transition/CSSHidingTransition';
import Slideout from 'components/transition/Slideout';
import SlideoutContent from 'components/slideout/SlideoutContent';

import _ from 'underscore';

import VisTypeChooser from 'components/visTypeChooser';
import CompareChooser from 'components/compareChooser';
import RelatedInsights from 'lib/@waca/vis-recommender/src/js/related/RelatedInsights';
import { observer } from 'mobx-react';
import { LayoutBase, ReactLayoutImplBase } from '../base/LayoutBase';

import StringResources from 'nls/StringResources';

import ExploreContainerDnDHandler from 'util/ExploreContainerDnDHandler';
import { openDatasetPane } from 'util/SidePanelUtil';

import EmptyExploreContainer from './EmptyExploreContainer';

import RECOMMENDATION_PROPERTIES from 'constants/RECOMMENDATION_PROPERTIES';


import ExploreContainerToolbar from './ExploreContainerToolbar';

import ExploreContainerContent from './ExploreContainerContent';
import { openCardsPane } from 'util/SidePanelUtil';

/**
 * Class representing view implementation (using react) for ExploreContainer layout view
 * @extends ReactLayoutImplBase
 */
@observer
class ExploreContainerView extends ReactLayoutImplBase {
	@observable activateDropTarget = false;
	constructor(props) {
		super(props);
		this.boardModel = this._initBoardModel(props);
		this.cardListRef = null;
		this.listeners = [];

		this.logger = props.services.biGlass.glassContext.getCoreSvc('.Logger');

		this.startingPointsVM = props.model.startingPointsModel;
		this._initDataSource();
		this._initVisTypes();

		//Trigger resize when slots shown/hidden
		mobx.reaction(() => {
			return this.props.model.exploreVM.showSlots;
		}, () => {
			this.layoutOwner.resizeChildren(350);	//Animation is 300ms - add 50ms for refresh time
		});
	}

	componentDidMount() {
		const options = {
			domNode: this.domNode,
			className: 'dndHover',
			dashboardApi: this.props.dashboardApi,
			addFragment: this.model.addFragment.bind(this.model),
			fragmentParentId: this.layoutOwner.id,
			exploreVM: this.model.exploreVM,
			activate: () => this.activateDropTarget = true,
			deactivate: () => this.activateDropTarget = false,
			glassContext: this.props.services.biGlass.glassContext
		};
		this.props.services.getSvc('.DndManager').then(
			(dndManager) => {
				this._dropTargetHandle = dndManager && dndManager.addDropTarget(
					this.domNode,
					new ExploreContainerDnDHandler(options)
				);
			}
		);
		this.model.exploreVM.setExploreContainerNode(this.domNode);
	}

	componentWillUnmount() {
		if( this._dropTargetHandle ) {
			this._dropTargetHandle.remove();
		}
	}

	_handleDeleteKey(event) {

		//This is needed to prevent the event from being reused. Without this, the handled property will always be true once it is set.
		event.persist();

		//If the event has been previously handled, we want to ignore the event
		if (event.handled || (event.target.tagName && event.target.tagName.match(/input|textarea|select/igm))) {
			return;
		}

		let canDelete = false;
		//We will prevent delete action if we are on the StartingPoints card
		switch(event.key) {
			case 'Delete':
			case 'Backspace':
				canDelete = !this.model.exploreVM.isStartingPointsDisplayed();
				break;
		}

		if (canDelete) {
			const containsLiveWidget = this.model.exploreVM.selectedWidgets.find(widget => widget.getType() !== 'placeholder');
			if(containsLiveWidget && this.model.exploreVM.isCompare()) {
				const visIds = this.model.exploreVM.selectedWidgets.map(widget => widget.getId());
				this.model.deleteVis(visIds);
			} else {
				this.model.deleteCards([this.model.exploreVM.displayedCard]);
			}
			this.model.exploreVM.setFocusOnExploreContainer();
		}
	}

	// TODO: [livewidget_cleanup] refactoring
	// (a)should refactor to not access boardModel / (b) should be sync
	_initBoardModel(props) {
		return Promise.resolve(props.layoutController.boardModel);
	}

	_initDataSource() {
		this.boardModel.then(
			(boardModel) => {
				const sourceList = this._getSourceModels(boardModel);
				sourceList && sourceList.forEach(sourceModel => {
					const shapingModel = sourceModel.get('shaping');
					shapingModel.on('shapingmodel:changed', this._onShapingChanges, this);
				});
				if (this.props.dashboardApi && (!this.props.dashboardApi.getActiveDataSourceId())) {
					// We are in constructor of ExploreView and do not know yet if there are cards to display or not.
					// If the dashboard spec has multiple data sources, by default take the first one.
					const useFirstDataSourceAsActive = true;
					this._updateDataSources(boardModel, useFirstDataSourceAsActive);
				}
				// This event is fired when data source changes
				boardModel.on('change:dataSources', this._onDataSourcesChanges.bind(this, boardModel), this);
			}
		);
	}

	_onDataSourcesChanges(boardModel, event){
		if (event.name) {
			if(event.name === 'add') {
				if(event.value){
					// --Add a datasource case
					//First register shaping event
					const shapingModel = event.value.get && event.value.get('shaping');
					shapingModel && shapingModel.on('shapingmodel:changed', this._onShapingChanges, this);

					// The new source is added at the end of of the source list,
					// so the active data source in this case is the last data source
					this._updateDataSources(boardModel);
				}
			} else if(event.name === 'remove'){
				if(event.value){
					// --Remove a datasource case
					// If the removed datasource is not current active datasource, do nothing
					// If the removed datasource is current active datasource, update active datasource
					if(event.value.id === this.props.model.exploreVM.activeDataSourceId){
						this._updateDataSources(boardModel);
					}
				}
			} else if(event.name === 'state' && event.value === 'ready'){
				if(event.sender){
					// --reload a dataset case
					// If the reloaded datasource is not current active datasource, do nothing
					// If the reloaded datasource is current active datasource, fetch fields again
					if(event.sender.id === this.props.model.exploreVM.activeDataSourceId){
						this.startingPointsVM.refreshStartingPoints();
					}
				}
			}
		}
	}

	_onShapingChanges(event){
		//If fields has not been fetched, we do nothing with the calculation changes
		if(!this.startingPointsVM.fields.hasFetchedFields()) return;

		if(event.action){
			switch(event.action){
				case 'ADD':
					this._addColumn(event.notification);
					this._clearDataSmartsServiceCache();
					break;
				case 'SET':
					this._changeProperties(event.notification);
					this._clearDataSmartsServiceCache();
					break;
				case 'REMOVE':
					this._removeColumn(event.notification);
					this._clearDataSmartsServiceCache();
					break;
				default:
					break;
			}
		}
	}

	_clearDataSmartsServiceCache() {
		const module = this.props.model.exploreVM.activeDataSourceModule;
		const sourceModuleId =  module ?  module.value.getAssetId() : null;
		if(sourceModuleId){
			this.props.model.exploreVM.getSmartsRecommenderWrapper().smartsRelationshipStrengthService.removeEntriesFromCache(sourceModuleId);
		}
	}

	_jsonParseString(value) {
		// Refer to `_createDebouncedShapingNotification` in shapingModelManager.js in Dashboard-livewidget
		// The value might be a JSON string or just a string - we have no way of knowing.
		if (typeof value === 'string') {
			try {
				return JSON.parse(value.trim());
			} catch (e) {
				return value;
			}
		}

		return value;
	}

	_addColumn(notification){
		const {
			newValue = {}
		} = notification || {};

		let newItem = this._jsonParseString(newValue);
		if(newItem.queryItem){
			newItem = newItem.queryItem;
		}

		const id = newItem.idForExpression;
		const name = newItem.label;
		if(id && name){
			this.startingPointsVM.fields.addToAllFields({
				id,
				name
			});
		}
	}

	_changeProperties(notification){
		if(notification && notification.notifier){
			this.startingPointsVM.refreshStartingPoints();
		}
	}

	_removeColumn(notification){
		const {
			oldValue = {}
		} = notification || {};

		let removedItem = this._jsonParseString(oldValue);
		if(removedItem.queryItem){
			removedItem = removedItem.queryItem;
		}
		const id = removedItem.idForExpression;
		if(id){
			this.startingPointsVM.fields.deleteField(id);
		}
	}

	/**
	 * @TODO livewisget_cleanup - not to access boardModel though service to thet the id - should use feature instead
	 */
	_getSourceModels(boardModel){
		const dataSources = boardModel.get('dataSources');
		const sources = dataSources && dataSources.get('sources');
		return sources && sources.getModels();
	}

	_updateDataSources(boardModel, useFirstDataSourceAsActive = false) {
		//MRB: boardModel is not yet MobX, so need to track changes to data sources manually
		const activeDataSourceId = this._getActiveDataSourceId(this._getSourceModels(boardModel), useFirstDataSourceAsActive);

		//Assume on a new data source being added that it's the one the user wishes to see
		this.props.model.exploreVM.setActiveDataSourceId(activeDataSourceId);

		if (activeDataSourceId) {
			this.props.dashboardApi.setActiveDataSourceId(activeDataSourceId);
		}
	}

	_getActiveDataSourceId(sourceList, useFirstDataSourceAsActive){
		if(sourceList && sourceList.length){
			return useFirstDataSourceAsActive ? sourceList[0].id : sourceList[sourceList.length - 1].id;
		}
		return '';
	}

	render() {
		return this.getRenderMarkup();
	}

	getRenderMarkup() {
		const showVisTypePicker = this.props.model.exploreVM.showModalVisTypePicker;
		const toolBarVisPicker = this.props.model.exploreVM.toolbarVisPicker;
		const showRelatedVisPicker = this.props.model.exploreVM.showModalRelatedVisPicker;
		const showCompareSlideout = this.props.model.exploreVM.showCompareSlideout;

		let visTypeChooserMarkup;
		if(toolBarVisPicker) {
			const hasDataInActiveWidgets = this.props.model.exploreVM.hasDataInActiveWidgets();
			const selectedWidgetModel = this.props.model.exploreVM.selectedWidgetModel;

			let isVisTypeLocked = true;
			let selectedVisId = null;

			if (selectedWidgetModel) {
				isVisTypeLocked = selectedWidgetModel.get('visTypeLocked');
				selectedVisId = selectedWidgetModel.get('visId');
			}

			let recommendationVM = null;
			if (hasDataInActiveWidgets) {
				recommendationVM = this.props.model.exploreVM.alternativeRecommendationVM;
			}

			visTypeChooserMarkup = (
				<VisTypeChooser
					active={showVisTypePicker}
					className="explore__animation--slideDown"
					dashboardApi={this.props.dashboardApi}
					recommendationVM={recommendationVM}
					isVizTypeLocked={isVisTypeLocked}
					selectedVisId={selectedVisId}
					services={this.props.services}
					onVisTypeSelected={this._onVisTypeSelected}
					onRecommendationSelected={this._onRecommendationSelected}
					onClose={this._closeVisTypeChooser}
					additionalProperties={RECOMMENDATION_PROPERTIES}
				/>
			);
		} else {
			visTypeChooserMarkup = (
				<VisTypeChooser
					dashboardApi={this.props.dashboardApi}
					active={showVisTypePicker}
					className="explore__animation--slideDown"
					services={this.props.services}
					onVisTypeSelected={this._onVisTypeSelectedVisWidget}
					//TODO _alive - VisTypeChooser refers to layoutOwner where object might not be available, introduce this flag to indicate if the view is destroyed or not. perhaps we can use a more proper way to address this
					onClose={this._closeVisTypeChooser}
				/>
			);
		}

		return (
			<div tabIndex="-1" className='ba-theme-waca explore' ref={(node) => { this.domNode = node; }} onKeyDown={(event) => this._handleDeleteKey(event)}>
				{this.activateDropTarget ? <EmptyExploreContainer /> : undefined}
				<ExploreContainerToolbar
					exploreVM={this.props.model.exploreVM}
					boardModel={this.props.model.boardModel}
					dashboardApi={this.props.dashboardApi}
					onAddCard={this.onAddCard}
				/>
				<div className='explore-content'>
					<CSSHidingTransition
						in={!showVisTypePicker}
						classNames="explore__animation--fade"
						onEnter={()=> {}}
						onExited={()=> {}}>
						<ExploreContainerContent
							model={this.props.model}
							startingPointsVM={this.startingPointsVM}
							services={this.props.services}
							layoutOwner={this.layoutOwner}
							addVisCard={this.addVisCard}
							dashboardApi={this.props.dashboardApi}
						/>
					</CSSHidingTransition>
					{visTypeChooserMarkup}
					<Slideout
						active={showRelatedVisPicker}
						className="explore__animation--slideDown"
						onClickOutside={this._closeRelatedVisPicker}
					>
						<SlideoutContent
							title={StringResources.get('related_visualizations')}
							contentClassName={'related-slideout'}
						>
							<div className="vis-recommender-explore__content">
								<RelatedInsights
									dashboardApi={this.props.dashboardApi}
									recommendationVM={this.props.model.exploreVM.relatedRecommendationVM}
									onClickItem={this._onRelatedRecommendationSelected}
									onAddItem={this._onAddRelatedRecommendation}
									additionalProperties={RECOMMENDATION_PROPERTIES}
									onPromptingError={this._onPromptingError}
									logger={this.logger}
								/>
							</div>
						</SlideoutContent>
					</Slideout>
					<CompareChooser
						dashboardApi={this.props.dashboardApi}
						customRecommendationVM={this.props.model.exploreVM.compareCardCustomRecommendationVM}
						smartsRecommendationVM={this.props.model.exploreVM.compareCardSmartsRecommendationVM}
						selectedWidgetModel={this.props.model.exploreVM.selectedWidgetModel}
						active={showCompareSlideout}
						onItemSelected={this.onCompareCardItemSelected}
						onItemAdded={this.onCompareCardItemAdded}
						onClose={this._closeCompareSlideout}
						logger={this.logger}
					/>
				</div>
			</div>)
		;
	}

	onAddCard = (event) => {
		const layoutModel = this.layoutController.topLayoutModel;

		const createCard = (...params) => {
			const id = layoutModel.addVisCard(...params);

			if (id) {
				const exploreVM = this.props.model.exploreVM;
				this.props.model.exploreVM.canvasTabs.showTab('slots');
				exploreVM.setDisplayedToNewCard(id);
				this.domNode.focus();
			}
		};

		const action = {
			'single': () => createCard([null], 'addBlankSingleCard'),
			'compare': () => createCard([null, null], 'addBlankCompareCard', { model: { subType: 'compare' } }),
			'startingPoints': () => {
				const exploreVM = this.props.model.exploreVM;
				exploreVM.setDisplayedToStartingPoints();
			}
		}[event.type];

		action();
	}

	/**
	 * Add compare card
	 *
	 * @param {object} item - compare card item
	 * @param {array} item.widgets - widgets in the compare card
	 *
	 * @return {string} newly added compare card id
	 */
	_addCompareCard = (item) => {
		return this.layoutController.topLayoutModel.addVisCard(
			item.widgets.map(({ spec }) => {
				if (spec) {
					return {
						model: spec
					};
				} else {
					return {
						model: {
							type: 'placeholder',
							placeHolder: true,
							showTitle: undefined,
							titleMode: 'smartTitle',
							name: ''
						}
					};
				}
			}),
			'exploreSelectRelatedRecommendation',
			{
				model: { subType: 'compare' }
			}
		);
	};

	onCompareCardItemAdded = (item) => {
		const id = this._addCompareCard(item);

		if (!id) {
			return;
		}

		openCardsPane(this.props.dashboardApi);
	}

	onCompareCardItemSelected = (item) => {

		const id = this._addCompareCard(item);

		if (!id) {
			return;
		}

		const exploreVM = this.props.model.exploreVM;
		this.props.model.exploreVM.canvasTabs.showTab('slots');
		exploreVM.setDisplayedToNewCard(id);
		openCardsPane(this.props.dashboardApi);
		this._closeCompareSlideout();
	}

	_initVisTypes(){
		// TODO: we shouldn't be setting types on the model(from the view), need to improve
		const visDefinitions  = this.props.dashboardApi.getFeature('VisDefinitions');
		this.props.model.exploreVM.setVisTypes(visDefinitions.getList());
	}

	/**
	 * Add one or more visualizations to the exploration, in their own new tab
	 * @param {object|object[]} widgetSpecs - specs for the new visualization(s)
	 * @param {string} transactionIdPrefix - description of this operation
	 * @param {string} tabDisplay - specifies tab to display (slots/details)
	 */
	addVisCard = (widgetSpecs, transactionIdPrefix, tabDisplay = 'details') => {
		if(!_.isArray(widgetSpecs)) {
			widgetSpecs = [widgetSpecs];
		}

		const selectAfterAdd = [
			'exploreSelectRelatedRecommendation',
			'exploreSelectCometRecommendation',
			'startingPointsSelectRecommendation',
			'startingPointsCreate'
		].indexOf(transactionIdPrefix) !== -1;

		const options = {
			sender: transactionIdPrefix,
			selectAfterAdd
		};
		this.props.model.exploreVM.canvasTabs.showTab(tabDisplay);
		this.props.model.addVisCard(widgetSpecs, transactionIdPrefix, options);
	}

	_closeVisTypeChooser = () => {
		this.props.model.exploreVM.setShowModalVisTypePicker(false);
	}

	_closeRelatedVisPicker = () => {
		this.props.model.exploreVM.setShowModalRelatedVisPicker(false);
	}

	_closeCompareSlideout = () => {
		this.props.model.exploreVM.setShowCompareSlideout(false);
	}

	_onAddRelatedRecommendation = (recommendation) => {
		if (recommendation && recommendation.widgets && recommendation.widgets[0]) {
			this._addCardByRecommendation(recommendation.widgets[0], 'addRelatedRecommendation');
			openCardsPane(this.props.dashboardApi);
		}
	}

	_onPromptingError(recommendation) {
		recommendation.isStaticWidget = true;
	}

	_addCardByRecommendation(recommendation, transactionIdPrefix) {
		const widgetSpecs = {
			model: Object.assign({
				type: 'live',
				visTypeLocked: true
			}, recommendation.spec),
			layoutProperties: {
				isStaticWidget: recommendation.isStaticWidget
			}
		};
		return this.addVisCard(widgetSpecs, transactionIdPrefix);
	}

	_onRelatedRecommendationSelected = (recommendation, index) => {
		this._closeRelatedVisPicker();
		const selectedRecommendation = this.props.model.exploreVM.relatedRecommendationVM.getRecommendations()[index];
		if (selectedRecommendation && recommendation.widgets && recommendation.widgets[0]) {
			this._addCardByRecommendation(recommendation.widgets[0], 'exploreSelectRelatedRecommendation');
			return openCardsPane(this.props.dashboardApi);
		}
	}
	// we only support recommendation visulization for the first selected widget vis for now
	_onRecommendationSelected = (recommendation) => {
		this._closeVisTypeChooser();
		this._closeRelatedVisPicker();
		const isSetAuto = recommendation.isAutoModeSwitcher;
		let visTypeLocked = true;
		const widgetModel = this.props.model.exploreVM.selectedWidgetModel;
		if(widgetModel){
			visTypeLocked = !!widgetModel.get('visTypeLocked');
		}
		//Change vis type if user is now manually selecting a viz Or
		//the current viz was manually selected and auto-recommend mode is on
		const shouldChangeVisType = isSetAuto ? visTypeLocked : true;
		if (shouldChangeVisType) {
			this.props.model.exploreVM.changeVisType(recommendation, isSetAuto);
		}
	}

	//handle only the first vis for now
	_onVisTypeSelected = (visTypeId) => {
		this._closeVisTypeChooser();
		this.props.model.exploreVM.changeFixedVisType(visTypeId);
	}

	_onVisTypeSelectedVisWidget = (visTypeId) => {
		this._closeVisTypeChooser();
		const visDestination = this.layoutOwner.model.items[0].visDestination;
		this.createVisFromVisType(visTypeId, visDestination);
		this.model.boardModel._hookupCustomDataHandler();
		const dashboardApi = this.props.dashboardApi;
		openDatasetPane(dashboardApi);
	}

	createVisFromVisType(visTypeId) {
		const widgetSpec = {
			model: {
				type: 'live',
				visId: visTypeId,
				visTypeLocked: true
			}
		};
		this.props.model.exploreVM.widgetInstance.createVis(widgetSpec);
		this.props.model.exploreVM.setToolBarVisTypePicker(true, null);
	}
}

const ExploreContainer = class ExploreContainer extends LayoutBase {
	constructor(props){
		super(props);
		this.Component = ExploreContainerView;
	}

};


export default ExploreContainer;



// WEBPACK FOOTER //
// ./src/js/layout/explorecontainer/ExploreContainer.js